import { FiArrowLeft } from 'react-icons/fi';
import { BiImport } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import { useState } from 'react';

import AlertHelper from 'helpers/AlertHelper';
import NotificationHelper from 'helpers/NotificationHelper';
import Header from 'components/ui/Header';
import SearchInput from 'components/ui/inputs/SearchInput';
import DotMenu from 'components/ui/DotMenu';
import DotMenuItem from 'components/ui/DotMenuItem';

import DatasetApi from 'api/dataset';
import ExporterApi from 'api/exporter';

import { FaTrash, FaFileExport } from 'react-icons/fa';

const DbHeader = ({ db, onSearch }) => {
  const { t } = useTranslation();
  const { params } = useRouteMatch();
  const history = useHistory();
  const [exporting, setExport] = useState(false);

  const handleExportCsv = async () => {
    const { status } = params;

    try {
      setExport(true);
      await ExporterApi.create({
        type: 'DATABASES',
        input: {
          db_id: db.id,
          status,
        },
      });

      history.push(`/exporter/databases`);
    } catch (error) {
      NotificationHelper.snack(t(`messages.unexpected_error`));
      console.error(error);
    } finally {
      setExport(false);
    }
  };

  const renderExportBtn = () => {
    if (exporting) {
      return (
        <div className="btn btn-outline-light">
          <div>{t('keys.waiting')}</div>
        </div>
      );
    }

    return (
      <button type="button" onClick={handleExportCsv} className="btn btn-outline-light">
        <div>{t('actions.export_csv')}</div>
      </button>
    );
  };

  const schema = db.schema || {};

  const { dbId } = useParams();
  const queryClient = useQueryClient();

  const mutationParams = {
    onSuccess: () => {
      queryClient.invalidateQueries('datasets');
    },
  };

  const removeAllMutation = useMutation(() => DatasetApi.deleteAll(dbId), mutationParams);

  const handleRemoveAll = async () => {
    const result = await AlertHelper.confirmDanger({
      title: t('database.alerts.remove_all.title'),
      html: t('database.alerts.remove_all.text'),
    });

    if (result.value) {
      removeAllMutation.mutate();
    }
  };

  return (
    <Header>
      <div className="d-flex  align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div className="me-4">
            <Link to="/databases">
              <FiArrowLeft size={24} />
            </Link>
          </div>

          <div>
            <div>
              <small>
                <Link to="/databases">databases</Link>
              </small>
            </div>
            <h2 className="m-0 p-0 fw-lighter">{schema.title}</h2>
          </div>
        </div>

        <div style={{ minWidth: 400 }}>
          <SearchInput id="search-db" onSearch={onSearch} />
        </div>

        <div className="d-flex justify-content-end align-items-center">
          <Link className="btn btn-outline-primary d-flex align-items-center" title={t('actions.import')} to={`/importer/databases/${db.id}`}>
            <BiImport size={22} />
          </Link>

          {renderExportBtn()}

          <Link className="btn btn-outline-light padding ms-3" to={`/databases/${db.id}/add`}>
            {t('actions.add')}
          </Link>

          <div className="ms-3">
            <DotMenu vertical color="#fff">
              <DotMenuItem onClick={handleRemoveAll} type="button">
                <div className="d-flex align-items-center p-1 text-danger">
                  <FaTrash /> <div className="ms-2">{t('actions.clean_all')}</div>
                </div>
              </DotMenuItem>
            </DotMenu>
          </div>
        </div>
      </div>
    </Header>
  );
};

export default DbHeader;
